import { useMemo, useState } from "react";
import { Field, Label } from "@headlessui/react";
import RequiredField from "../../components/RequiredField";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ModalForm from "../../components/ModalForm";
import Password from "../../components/input/Password";
import Select from "../../components/input/Select";
import { passwordInputClassName, submitAndReset } from "./PasswordConfirmDialog";
import { SecretManagerViewModel, useSetSecretAccessMutation } from "./secretsApiSlice";

interface Props {
	availableSuperAdministrators: SecretManagerViewModel[];
	onClose: () => void;
}

/**
 * Grants another Super Administrator access to manage secrets.
 */
export default function AddManagerDialog({ availableSuperAdministrators, onClose }: Props) {
	const [userId, setUserId] = useState<number>();
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [setSecretAccess, { reset }] = useSetSecretAccessMutation();

	const options = useMemo(
		() =>
			[...availableSuperAdministrators]
				.sort((a, b) => displayName(a).localeCompare(displayName(b)))
				.map((admin) => ({ value: admin.id, label: optionLabel(admin) })),
		[availableSuperAdministrators]
	);

	function handleConfirm() {
		if (userId === undefined) return;

		setError("");
		setIsSubmitting(true);
		submitAndReset(
			setSecretAccess({ userId, canManageSecrets: true, password }).unwrap(),
			reset
		)
			.then(() => onClose())
			.catch((message: string) => {
				setPassword("");
				setError(message);
			})
			.finally(() => setIsSubmitting(false));
	}

	return (
		<ModalForm
			open
			title="Add Secret Manager"
			form={
				<div className="pt-2 space-y-4">
					<AlertMessage
						type={AlertLevel.Error}
						message={error}
						showIcon
						onClear={() => setError("")}
					/>
					{options.length === 0 ? (
						<p className="text-sm text-gray-600 mb-2">
							There are no other Super Administrators who can be granted access. You
							can't grant access to yourself.
						</p>
					) : (
						<>
							<p className="text-sm text-gray-600 mb-2">
								Only Super Administrators can be granted access, and you can't grant
								access to yourself. Once access is granted, they will be able to
								change secrets and decide who else can.
							</p>
							<Select
								simpleListBoxId="secrets-new-manager"
								label="Super Administrator"
								prompt="Select a Super Administrator..."
								requiredField
								value={userId}
								setValue={(v) =>
									setUserId(v === undefined || v === "" ? undefined : Number(v))
								}
								options={options}
								simpleListBoxClasses="w-full"
							/>
							<Field>
								<Label
									htmlFor="secrets-your-password"
									className="font-medium text-sm">
									Enter Your Password
									<RequiredField />
								</Label>
								<Password
									id="secrets-your-password"
									name="secrets-your-password"
									placeholder="your password"
									autoComplete="new-password"
									className={passwordInputClassName}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
							</Field>
						</>
					)}
				</div>
			}
			confirmLabel="Grant Access"
			disableConfirm={userId === undefined || !password || isSubmitting}
			onConfirm={handleConfirm}
			onCancel={onClose}
		/>
	);
}

export function displayName(admin: SecretManagerViewModel) {
	return admin.fullName || admin.userName;
}

function optionLabel(admin: SecretManagerViewModel) {
	return admin.fullName ? `${admin.fullName} (${admin.userName})` : admin.userName;
}
